import React, { useRef, useState, useEffect } from 'react';
import { mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import { AlignLeft, AlignCenter, AlignRight, Trash2 } from 'lucide-react';

const ResizableImageNodeView = ({ node, updateAttributes, selected, editor, deleteNode }) => {
  const { src, alt, title, width, height, align, spacing } = node.attrs;
  const imgRef = useRef(null);
  const contentRef = useRef(null);
  const toolbarRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [toolbarStyle, setToolbarStyle] = useState({});

  useEffect(() => {
    if (!selected) {
      setToolbarStyle({});
      return;
    }

    const updatePosition = () => {
      const content = contentRef.current;
      const toolbar = toolbarRef.current;
      if (!content || !toolbar) return;

      const contentRect = content.getBoundingClientRect();
      const toolbarWidth = toolbar.offsetWidth || 230;
      const margin = 8;
      const editorEl = content.closest('.tiptap-editor-content') || content.closest('.ProseMirror');
      if (!editorEl) return;

      const editorRect = editorEl.getBoundingClientRect();
      let targetViewportLeft = contentRect.left + (contentRect.width / 2) - (toolbarWidth / 2);
      const minViewportLeft = editorRect.left + margin;
      const maxViewportLeft = editorRect.right - toolbarWidth - margin;
      const clampedViewportLeft = Math.max(minViewportLeft, Math.min(targetViewportLeft, maxViewportLeft));
      const localLeft = clampedViewportLeft - contentRect.left;

      setToolbarStyle({
        left: `${localLeft}px`,
        transform: 'none',
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    const timeoutId = setTimeout(updatePosition, 50);

    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      clearTimeout(timeoutId);
    };
  }, [selected, align, width]);

  const startResize = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = imgRef.current.clientWidth;
    const startHeight = imgRef.current.clientHeight;

    const onMouseMove = (moveEvent) => {
      let newWidth = startWidth;
      let newHeight = startHeight;
      
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      if (direction.includes('e')) newWidth = startWidth + dx;
      if (direction.includes('w')) newWidth = startWidth - dx;
      if (direction.includes('s')) newHeight = startHeight + dy;
      if (direction.includes('n')) newHeight = startHeight - dy;

      const aspectRatio = startWidth / startHeight;
      newHeight = newWidth / aspectRatio;
      
      if (newWidth < 50) newWidth = 50;
      if (newHeight < 50) newHeight = 50;

      imgRef.current.style.width = `${newWidth}px`;
      imgRef.current.style.height = `${newHeight}px`;
      setIsResizing(true);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setIsResizing(false);
      updateAttributes({
        width: imgRef.current.style.width,
        height: imgRef.current.style.height,
      });
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleAlign = (alignment) => {
    updateAttributes({ align: alignment });
  };
  
  const handleSpacing = (e) => {
    updateAttributes({ spacing: e.target.value });
  };

  const marginValue = spacing || '1rem';
  let alignClass = 'align-none';
  if (align === 'left') alignClass = 'align-left';
  else if (align === 'right') alignClass = 'align-right';
  else if (align === 'center') alignClass = 'align-center';

  const handleDragStart = (e) => {
      if (isResizing) {
          e.preventDefault();
      }
  };

  return (
    <NodeViewWrapper as="span" className={`tiptap-image-wrapper ${alignClass}`} style={{ '--img-spacing': marginValue }} onDragStart={handleDragStart}>
      <span ref={contentRef} className="tiptap-image-content">
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          title={title}
          className={selected ? 'is-selected' : ''}
          style={{ width: width || 'auto', height: height || 'auto' }}
          draggable="false"
        />
        {selected && (
          <>
            <div onMouseDown={(e) => startResize(e, 'nw')} className="image-resizer-handle nw" />
            <div onMouseDown={(e) => startResize(e, 'ne')} className="image-resizer-handle ne" />
            <div onMouseDown={(e) => startResize(e, 'sw')} className="image-resizer-handle sw" />
            <div onMouseDown={(e) => startResize(e, 'se')} className="image-resizer-handle se" />
            <div ref={toolbarRef} className="image-align-toolbar" style={toolbarStyle}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleAlign('left'); }} title="Wrap Left" className={`image-align-btn ${align === 'left' ? 'is-active' : ''}`}><AlignLeft size={16} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleAlign('center'); }} title="Center" className={`image-align-btn ${align === 'center' ? 'is-active' : ''}`}><AlignCenter size={16} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleAlign('right'); }} title="Wrap Right" className={`image-align-btn ${align === 'right' ? 'is-active' : ''}`}><AlignRight size={16} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleAlign('none'); }} title="Inline" className={`image-align-btn ${!align || align === 'none' ? 'is-active' : ''}`}><span style={{fontSize: 12, fontWeight: 'bold', padding: '0 4px'}}>Inline</span></button>
              <div className="image-align-separator" />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); deleteNode(); }} title="Delete Image" className="image-align-btn delete-btn"><Trash2 size={16} /></button>
              <div className="image-align-separator" />
              <input 
                type="text" 
                title="Spacing (e.g., 1rem or 15px)"
                value={spacing || '1rem'} 
                onChange={handleSpacing} 
                onMouseDown={e => e.stopPropagation()}
                className="image-spacing-input"
              />
            </div>
          </>
        )}
      </span>
    </NodeViewWrapper>
  );
};

export const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: element => element.getAttribute('width') || element.style.width,
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        }
      },
      height: {
        default: null,
        parseHTML: element => element.getAttribute('height') || element.style.height,
        renderHTML: attributes => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        }
      },
      align: {
        default: 'none',
        parseHTML: element => element.getAttribute('data-align') || 'none',
        renderHTML: attributes => {
          if (!attributes.align || attributes.align === 'none') return {};
          return { 'data-align': attributes.align };
        }
      },
      spacing: {
        default: '1rem',
        parseHTML: element => element.getAttribute('data-spacing') || '1rem',
        renderHTML: attributes => {
          if (!attributes.spacing || attributes.spacing === '1rem') return {};
          return { 'data-spacing': attributes.spacing };
        }
      }
    };
  },
  
  renderHTML({ node, HTMLAttributes }) {
      const { align, spacing, width, height } = node.attrs;
      let styleStr = '';
      let margin = spacing || '1rem';
      
      if (width) styleStr += `width: ${width}; `;
      if (height) styleStr += `height: ${height}; `;
      
      if (align === 'left') {
          styleStr += `float: left; margin: 0 ${margin} ${margin} 0; `;
      } else if (align === 'right') {
          styleStr += `float: right; margin: 0 0 ${margin} ${margin}; `;
      } else if (align === 'center') {
          styleStr += `display: block; margin: 0 auto ${margin} auto; `;
      } else {
          styleStr += `margin: 0 0 ${margin} 0; `;
      }
      
      const modifiedAttrs = {
          ...HTMLAttributes,
          'data-align': align,
          'data-spacing': spacing,
          width,
          height,
          style: styleStr.trim() || undefined
      };
      
      return ['img', mergeAttributes(this.options.HTMLAttributes, modifiedAttrs)];
  },
  
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  }
});
